import React, { useEffect, useRef, useState } from "react";

// Pins `src` as a fixed full-viewport background for the entire page and
// seeks its currentTime to match scroll progress through the whole
// document (0% at top, 100% at bottom) — one continuous morph tied to the
// full page, not a per-section effect. Content renders in normal document
// flow above it; sections should be transparent/glass so the video reads
// through, per the design reference in .stitch/designs.
//
// The video itself is never shown directly: iOS Safari only repaints a
// <video> element's visual layer while it's actively playing, so seeking
// `currentTime` while paused updates the property but silently freezes the
// screen at whatever frame was last playing. Drawing each frame onto a
// <canvas> instead forces an explicit read of the decoded frame every tick,
// sidestepping that repaint quirk entirely.
const FullPageScrubVideo = ({ src, poster }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [reducedMotion] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );

  useEffect(() => {
    if (reducedMotion) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    const ctx = canvas.getContext("2d");

    // iOS WebKit (incl. Chrome on iPhone) often won't load actual frame
    // data past metadata for `preload="auto"` until an explicit play()
    // call unlocks it — waiting for a "loadeddata" event before calling
    // play() deadlocks, since that event itself depends on play() having
    // already been called. Call play() immediately instead.
    const primePlayback = () => {
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.then(() => video.pause()).catch(() => {});
      }
    };
    primePlayback();

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const resizeCanvas = () => {
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
    };
    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);

    const drawFrame = () => {
      const vw = video.videoWidth;
      const vh = video.videoHeight;
      if (!vw || !vh) return;
      const cw = canvas.width;
      const ch = canvas.height;
      const videoRatio = vw / vh;
      const canvasRatio = cw / ch;
      let sx, sy, sw, sh;
      if (videoRatio > canvasRatio) {
        sh = vh;
        sw = vh * canvasRatio;
        sx = (vw - sw) / 2;
        sy = 0;
      } else {
        sw = vw;
        sh = vw / canvasRatio;
        sx = 0;
        sy = (vh - sh) / 2;
      }
      ctx.drawImage(video, sx, sy, sw, sh, 0, 0, cw, ch);
    };

    let rafId;
    const renderLoop = () => {
      drawFrame();
      rafId = requestAnimationFrame(renderLoop);
    };
    rafId = requestAnimationFrame(renderLoop);

    // On a slow/real connection, `currentTime` can seek past what's actually
    // downloaded. Clamp the target to the buffered range so scrubbing never
    // outruns the download — worst case it lags a beat behind scroll on
    // slow networks, which reads far better than freezing on a stale frame.
    const clampToBuffered = (target) => {
      const { buffered } = video;
      let bufferedEnd = 0;
      for (let i = 0; i < buffered.length; i++) {
        if (buffered.start(i) <= target || buffered.start(i) <= bufferedEnd) {
          bufferedEnd = Math.max(bufferedEnd, buffered.end(i));
        }
      }
      return bufferedEnd > 0 ? Math.min(target, bufferedEnd) : target;
    };

    let ticking = false;
    const scrub = () => {
      ticking = false;
      if (!video.duration) return;
      const doc = document.documentElement;
      const total = doc.scrollHeight - window.innerHeight;
      const progress = total > 0 ? Math.min(1, Math.max(0, window.scrollY / total)) : 0;
      video.currentTime = clampToBuffered(progress * video.duration);
    };
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(scrub);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    video.addEventListener("progress", onScroll);
    scrub();
    return () => {
      cancelAnimationFrame(rafId);
      video.removeEventListener("progress", onScroll);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      window.removeEventListener("resize", resizeCanvas);
    };
  }, [reducedMotion]);

  if (reducedMotion) {
    return (
      <div className="fixed inset-0 -z-10 h-screen w-screen overflow-hidden">
        <img src={poster} alt="" className="h-full w-full object-cover" />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-ocean-dark/45 via-ocean-dark/25 to-ocean-dark/50" />
      </div>
    );
  }

  return (
    <div className="fixed inset-0 -z-10 h-screen w-screen overflow-hidden">
      <video
        ref={videoRef}
        src={src}
        poster={poster}
        muted
        playsInline
        preload="auto"
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          // Not literally 0: iOS WebKit (incl. Chrome on iPhone) treats
          // opacity:0 + near-zero size as "not visible" and throttles or
          // stops decoding new frames, which froze the canvas copy below.
          // A visually imperceptible but nonzero opacity at full size keeps
          // it decoding while staying hidden under the canvas.
          opacity: 0.01,
          pointerEvents: "none",
        }}
      />
      <canvas ref={canvasRef} className="absolute inset-0 h-full w-full object-cover" />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-ocean-dark/60 via-ocean-dark/40 to-ocean-dark/70" />
    </div>
  );
};

export default FullPageScrubVideo;
